import { useCallback } from 'react';
import { INITIAL_CONTEXT_MENU_STATE } from '../constants';
import { getTagDisplay } from '../utils';
import { insertTagAtMention, removeMentionTrigger, duplicateTagAtPosition } from '../tiptapEditorUtils';
import type { AvailableInput, PromptEditorContextMenuState } from '../types';
import type { Dispatch, SetStateAction } from 'react';
import type { Editor } from '@tiptap/react';
import type { PromptDisplayMode } from 'web';

interface UseEditorActionsProps {
  editor: Editor | null;
  availableInputs: AvailableInput[];
  displayMode: PromptDisplayMode;
  contextMenu: PromptEditorContextMenuState;
  setContextMenu: Dispatch<SetStateAction<PromptEditorContextMenuState>>;
}

export const useEditorActions = ({
  editor,
  availableInputs,
  displayMode,
  contextMenu,
  setContextMenu,
}: UseEditorActionsProps) => {
  // Close context menu - handles all menu types
  const closeContextMenu = useCallback(() => {
    if (contextMenu.type === 'mention' && editor) {
      // Convert "@" tag back to regular text when dismissing the menu
      removeMentionTrigger(editor, true);
    }
    setContextMenu(INITIAL_CONTEXT_MENU_STATE);
  }, [contextMenu.type, editor, setContextMenu]);

  // Replace the mention trigger "@" tag with the selected input's tag
  const insertInputTag = useCallback(
    (input: AvailableInput) => {
      if (!editor) return;

      const inputInfo = availableInputs.find((i) => i.key === input.key);
      if (!inputInfo) return;

      // Insert the tag at the mention position
      insertTagAtMention({
        editor,
        tagKey: input.key,
        ...getTagDisplay(inputInfo, displayMode),
      });

      setContextMenu(INITIAL_CONTEXT_MENU_STATE);
    },
    [editor, displayMode, availableInputs, setContextMenu],
  );

  // Copy the tag using ProseMirror's serializer + navigator.clipboard API
  const handleTagCopy = useCallback(
    async (_e?: React.MouseEvent<HTMLElement>) => {
      if (contextMenu.type !== 'tag' || !editor) return;

      const { tagKey, tagPos } = contextMenu;
      await navigator.clipboard.writeText(`{{${tagKey}}}`);
      // Focus the editor and place the cursor right after the copied tag
      editor
        .chain()
        .focus()
        .setTextSelection(tagPos + 1)
        .run();
      setContextMenu(INITIAL_CONTEXT_MENU_STATE);
    },
    [contextMenu, setContextMenu, editor],
  );

  // Duplicate the tag from context menu and close menu
  const handleTagDuplicate = useCallback(() => {
    if (!editor || contextMenu.type !== 'tag' || contextMenu.tagPos < 0) return;

    duplicateTagAtPosition({
      editor,
      tagKey: contextMenu.tagKey,
      tagPos: contextMenu.tagPos,
      availableInputs,
      displayMode,
    });
    setContextMenu(INITIAL_CONTEXT_MENU_STATE);
  }, [editor, contextMenu, availableInputs, displayMode, setContextMenu]);

  // Delete the tag at the stored position
  const handleTagDelete = useCallback(() => {
    if (!editor || contextMenu.type !== 'tag' || contextMenu.tagPos < 0) return;

    editor
      .chain()
      .focus()
      .deleteRange({ from: contextMenu.tagPos, to: contextMenu.tagPos + 1 })
      .run();

    setContextMenu(INITIAL_CONTEXT_MENU_STATE);
  }, [editor, contextMenu, setContextMenu]);

  return {
    insertInputTag,
    handleTagCopy,
    handleTagDuplicate,
    handleTagDelete,
    closeContextMenu,
  };
};
